export const arenaBackgroundIds = [
  "garden-glow",
  "moonlit-pond",
  "sunset-dunes",
  "cloudtop-park",
] as const;

export type ArenaBackgroundId = (typeof arenaBackgroundIds)[number];

export interface ArenaBackgroundAccent {
  readonly alpha: number;
  readonly color: number;
  readonly heightRatio: number;
  readonly widthRatio: number;
  readonly xRatio: number;
  readonly yRatio: number;
}

export interface ArenaBackgroundConfig {
  readonly accents: readonly ArenaBackgroundAccent[];
  readonly backgroundColor: number;
  readonly boundaryColor: number;
  readonly boundaryOutlineColor: number;
  readonly fieldColor: number;
  readonly id: ArenaBackgroundId;
  readonly name: string;
}

const broadAccents: readonly Omit<ArenaBackgroundAccent, "color">[] = [
  {
    alpha: 0.12,
    heightRatio: 0.08,
    widthRatio: 0.26,
    xRatio: 0.15,
    yRatio: 0.2,
  },
  {
    alpha: 0.1,
    heightRatio: 0.12,
    widthRatio: 0.34,
    xRatio: 0.43,
    yRatio: 0.42,
  },
  {
    alpha: 0.12,
    heightRatio: 0.09,
    widthRatio: 0.28,
    xRatio: 0.78,
    yRatio: 0.24,
  },
  {
    alpha: 0.1,
    heightRatio: 0.13,
    widthRatio: 0.38,
    xRatio: 0.22,
    yRatio: 0.78,
  },
  {
    alpha: 0.11,
    heightRatio: 0.1,
    widthRatio: 0.31,
    xRatio: 0.7,
    yRatio: 0.72,
  },
];

const accents = (color: number): readonly ArenaBackgroundAccent[] =>
  broadAccents.map((accent) => ({ ...accent, color }));

export const arenaBackgroundConfigs: Readonly<
  Record<ArenaBackgroundId, ArenaBackgroundConfig>
> = {
  "garden-glow": {
    accents: accents(0x194f46),
    backgroundColor: 0x153c42,
    boundaryColor: 0xf3c969,
    boundaryOutlineColor: 0xffedab,
    fieldColor: 0x2b674d,
    id: "garden-glow",
    name: "Garden Glow",
  },
  "moonlit-pond": {
    accents: accents(0x153b59),
    backgroundColor: 0x102b46,
    boundaryColor: 0x4b8791,
    boundaryOutlineColor: 0x8cc5c5,
    fieldColor: 0x205a64,
    id: "moonlit-pond",
    name: "Moonlit Pond",
  },
  "sunset-dunes": {
    accents: accents(0xa85e4c),
    backgroundColor: 0x70424a,
    boundaryColor: 0xe4a55e,
    boundaryOutlineColor: 0xffd892,
    fieldColor: 0xc97c5d,
    id: "sunset-dunes",
    name: "Sunset Dunes",
  },
  "cloudtop-park": {
    accents: accents(0x8996bd),
    backgroundColor: 0x6075a0,
    boundaryColor: 0xc1b5df,
    boundaryOutlineColor: 0xf3e9ff,
    fieldColor: 0x93a7c9,
    id: "cloudtop-park",
    name: "Cloudtop Park",
  },
};

export const defaultArenaBackgroundId: ArenaBackgroundId = "garden-glow";

export const getDefaultArenaBackgroundId = (): ArenaBackgroundId =>
  defaultArenaBackgroundId;

const arenaBackgroundIdSet = new Set<string>(arenaBackgroundIds);

export const isArenaBackgroundId = (
  value: unknown,
): value is ArenaBackgroundId =>
  typeof value === "string" && arenaBackgroundIdSet.has(value);

export const getArenaBackgroundConfig = (
  id: ArenaBackgroundId,
): ArenaBackgroundConfig => arenaBackgroundConfigs[id];
